import { useEffect } from 'react';
import { useFirstTrade } from '@features/dashboard/hooks/useFirstTrade';
import { useAccount } from '@shared/state/accountStore';
import { useDateRangeStore } from '@shared/state/dateRangeStore';

/**
 * Side-effect hook (renders nothing) that anchors the *default* global date range's START to the
 * selected account's FIRST-trade day (END = today) — "from my first trade … to now". So a fresh
 * visitor immediately sees their whole history, and switching accounts re-anchors the start to that
 * account's first trade.
 *
 * Reads the account-scoped `/stats/first-trade` value and, once it resolves, calls
 * `seedFromFirstTrade(ymd)`. That action no-ops unless the range is still the auto-default (the user
 * hasn't picked one and nothing was restored from localStorage), so an explicit pick is never
 * clobbered. Mounted once, globally (in the Topbar). `firstTradeAt === null` (no trades) → nothing to
 * seed (the date picker is disabled separately). ISO instant → local `YYYY-MM-DD` to match the picker.
 */
export function useSeedDateRangeFromFirstTrade(): void {
  const account = useAccount();
  const firstTrade = useFirstTrade({ account: account || undefined });
  const seedFromFirstTrade = useDateRangeStore(s => s.seedFromFirstTrade);

  const firstTradeAt = firstTrade.data?.firstTradeAt ?? null;

  useEffect(() => {
    if (!firstTradeAt) return;
    const d = new Date(firstTradeAt);
    if (Number.isNaN(d.getTime())) return;
    const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    seedFromFirstTrade(ymd);
  }, [firstTradeAt, seedFromFirstTrade]);
}
